import type { FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';

import type {
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { sendAutocompleteRequest } from 'Core/actions/autocomplete';
import { assignLocation } from 'Core/actions/redirect';
import { autocompleteIsLoadingSelector, autocompleteTotalHitsSelector } from 'Core/selectors/autocomplete';
import { selectedVehicleSelector } from 'Core/selectors/fitmentSearch/index';
import { autocompletePreselectionSelector } from 'Core/selectors/preselection';
import { useDispatch, useSelector } from 'Core/store';
import { AutocompleteType, type AutocompleteTypes } from 'Models/autocompleteItem';
import type { AutocompleteItem, FacetValueBase } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import { groupByField } from 'Utils/array';
import { cloneSafe } from 'Utils/components';
import safeTemplateCall from 'Utils/safeTemplateCall';

import BrowseAllButton, { type BrowseAllButtonTemplateParams } from './dropdownItems/browseAllButton';
import Section, { Params as SectionParams, type SectionConfig } from './dropdownItems/section';

import 'Core/hooks/dropdown.scss';

const { defaultOrder, order, facetValueOrder } = requestConfig.autocomplete;
const autocompleteOrder = (order.length ? order : defaultOrder) as AutocompleteTypes[];

export type Params = {
  sections: RepeaterFunctionInvoker<SectionParams>;
  noResults?: boolean;
  noProductResults?: boolean;
  query?: string;
  browseAllButton: TemplateFunctionInvoker<BrowseAllButtonTemplateParams>;
};

type Props = {
  template: TemplateFunction<Params>;
  items: AutocompleteItem[];
  query: string;
  previouslySubmittedQuery: string;
  showDefaultSuggestions: boolean;
  useSuggestionsFilter?: true;
  setQuery: (query: string) => void;
  setSelection: (selection: FacetValueBase[], query?: string) => void;
  submit: () => void;
  onDropdownItemsReceived?: () => void;
};

const SearchDropdown: FunctionComponent<Props> = ({
  template,
  items,
  query,
  previouslySubmittedQuery,
  showDefaultSuggestions,
  useSuggestionsFilter,
  setQuery,
  setSelection,
  submit,
  onDropdownItemsReceived,
}) => {
  const dispatch = useDispatch();

  const preselection = useSelector(autocompletePreselectionSelector);
  const selectedVehicle = useSelector(selectedVehicleSelector);
  const totalHits = useSelector(autocompleteTotalHitsSelector);
  const isLoading = useSelector(autocompleteIsLoadingSelector);

  useEffect(() => {
    if (items.length) {
      onDropdownItemsReceived?.();
    }
  });

  const initialLoading = isLoading && items.length === 0;
  const nothingToShow = !query && items.length === 0;
  const queryIsNotChanged = !!query && previouslySubmittedQuery === query;
  const queryTooShort = !!query && query.length < 3;

  const hideDropdown = initialLoading || nothingToShow || queryIsNotChanged || queryTooShort;
  if (hideDropdown) {
    // nothingToShow: If query is empty and even no default suggestions to show

    // queryIsNotChanged: When land on search page after submitting the search form
    // we don't need to show dropdown on first click/focus

    const component = safeTemplateCall(
      template,
      { sections: [], browseAllButton: () => null },
      'SearchDropdown',
    );
    return cloneSafe(component, null, {
      appendedClasses: ['cm_autocomplete', 'cm_autocomplete__empty', 'empty'],
    });
  }

  const noResults = !isLoading && !!query && items.length === 0;
  const noProductResults = !items.filter((item) => item.type === 'product').length;

  // Don't show vehicle if any product doesn't fit it
  const vehicleString = items
    .filter((item) => item.type === AutocompleteType.Product)
    .some((item) => item.searchItem?.fitsTheVehicle === 'no')
    ? ''
    : selectedVehicle.toString();

  const browseAllButton: TemplateFunctionInvoker<BrowseAllButtonTemplateParams> = showDefaultSuggestions
    ? () => null
    : (templ) =>
        (
          <BrowseAllButton {...{ template: templ, query, submit, vehicleString }} key="browse-all" />
        ) as TemplateResult;

  const sections: RepeaterFunctionInvoker<SectionParams> = autocompleteOrder
    .reduce<SectionConfig[]>((propsArr, sectionsGroup) => {
      const itemsData = items.filter((item) => item.type === sectionsGroup);
      if (!itemsData.length) {
        return propsArr;
      }

      switch (sectionsGroup) {
        case 'text': {
          return [
            ...propsArr,
            {
              query,
              itemsData,
              vehicleString,
              templateName: sectionsGroup,
              title: !query ? 'Trending' : 'Suggestions',
              onClickHandler: ({ textValue }) => {
                const newQuery = textValue?.text;
                if (newQuery) {
                  if (!useSuggestionsFilter) {
                    setQuery(newQuery);
                  } else if (newQuery !== query) {
                    dispatch(sendAutocompleteRequest({ query: newQuery, selection: preselection }));
                    setQuery(newQuery);
                  }
                }
              },
              onMouseOverHandler: ({ textValue }) => {
                const newQuery = textValue?.text;
                if (useSuggestionsFilter && newQuery) {
                  // Change autocomplete products on hover and set active query for browse all button
                  dispatch(sendAutocompleteRequest({ query: newQuery, selection: preselection }));
                  setQuery(newQuery);
                }
              },
            },
          ];
        }
        case 'product': {
          return [
            ...propsArr,
            ...itemsToGroupedProps(itemsData, {
              totalHits,
              vehicleString,
              templateName: sectionsGroup,
              onClickHandler: ({ searchItem: { redirectUrl } = {} }) =>
                redirectUrl && dispatch(assignLocation(redirectUrl)),
              browseAllButton,
            }),
          ];
        }
        case 'facetValue': {
          const { ordered, rest } = orderedFacetSectionProps(itemsData, {
            vehicleString,
            templateName: sectionsGroup,
            onClickHandler: ({ facetValue }) => {
              if (facetValue) {
                if (facetValue.redirectUrl) {
                  dispatch(assignLocation(facetValue.redirectUrl));
                } else {
                  facetValue.productCategorySuggestion
                    ? setSelection([facetValue], query)
                    : setSelection([facetValue]);
                }
              }
            },
          });
          return [
            ...propsArr,
            ...facetValueOrder.map((title) => ordered[title] || { title, itemsData: [] }),
            ...rest,
          ];
        }
        default:
          console.error(`AutocompleteOrder includes '${sectionsGroup}' that is unknown`);
          return propsArr;
      }
    }, [])
    .map(
      (sectionProps) => (template) =>
        (
          <Section
            {...{
              ...sectionProps,
              template,
            }}
            key={sectionProps.title}
          />
        ) as TemplateResult,
    );

  return cloneSafe(
    safeTemplateCall(
      template,
      { sections, noProductResults, noResults, query, browseAllButton },
      'SearchDropdown',
    ),
    null,
    { appendedClasses: 'cm_autocomplete' },
  );
};

export default SearchDropdown;

function itemsToGroupedProps(
  items: AutocompleteItem[],
  commonProps: Omit<SectionConfig, 'itemsData' | 'title'>,
) {
  const groupedItems = groupByField(items, 'suggestionGroup');
  return Object.entries(groupedItems).reduce<SectionConfig[]>((result, [title, itemsData]) => {
    result.push({ ...commonProps, title, itemsData });
    return result;
  }, []);
}

type OrderedSections = {
  ordered: Record<string, SectionConfig>;
  rest: SectionConfig[];
};

function orderedFacetSectionProps(
  items: AutocompleteItem[],
  commonProps: Omit<SectionConfig, 'itemsData' | 'title'>,
) {
  return itemsToGroupedProps(items, commonProps).reduce<OrderedSections>(
    (result, props) => {
      if (props.title && facetValueOrder.includes(props.title)) {
        result.ordered[props.title] = props;
      } else {
        result.rest.push(props);
      }
      return result;
    },
    { ordered: {}, rest: [] },
  );
}
